import { useNavigate } from "react-router-dom";
import { Search, Armchair, Smartphone, QrCode, Radar, ShieldCheck, Award, MessageSquareText, ArrowRight } from "lucide-react";
import { Crest, FlagStrip, OfficialBadge } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import Seo from "@/components/Seo";

const STEPS = [
  { icon: Search, title: "Search your route", text: "Pick where you're travelling from and to, and your date. We show every GPRTU departure with live seat availability and the fare." },
  { icon: Armchair, title: "Choose your seat", text: "See the real seat plan for the coach and tap the exact seat you want. Add passenger names and phone numbers." },
  { icon: Smartphone, title: "Pay with Mobile Money", text: "Pay securely with MTN MoMo, Telecel Cash, AirtelTigo Money or a bank card. A prompt arrives on your phone to approve." },
  { icon: QrCode, title: "Get your e-ticket", text: "Your ticket and a scannable QR code arrive instantly by SMS and on-screen. No printing needed — your phone is your ticket." },
  { icon: Radar, title: "Board & track", text: "Show your QR code at the station to board. Follow your bus live on the map and watch the arrivals board at each station." },
];

const EXTRAS = [
  { icon: Award, title: "Earn miles", text: "Collect 1 mile for every GH₵ 1 you spend and redeem on future trips." },
  { icon: ShieldCheck, title: "Optional travel insurance", text: "Add affordable passenger cover at checkout, underwritten separately and confirmed by SMS." },
  { icon: MessageSquareText, title: "Ticket by SMS", text: "Even with no data, your ticket link and QR reach you on any phone." },
];

function Step({ i, icon: Icon, title, text }) {
  return (
    <div className="card-soft flex gap-4 p-5" data-testid={`how-step-${i}`}>
      <div className="flex flex-col items-center">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-blue-50 text-[#0052FF]"><Icon className="h-5 w-5" /></span>
        {i < 5 && <span className="mt-2 h-full w-px flex-1 bg-slate-200" aria-hidden />}
      </div>
      <div className="pb-2">
        <div className="label-xs text-[#0052FF]">Step {i}</div>
        <div className="font-display text-lg font-extrabold text-slate-900">{title}</div>
        <p className="mt-1 text-sm text-slate-600">{text}</p>
      </div>
    </div>
  );
}

export default function HowItWorks() {
  const nav = useNavigate();
  return (
    <div data-testid="how-it-works-page">
      <Seo title="How it works — book a GPRTU bus in 5 steps" description="See how Quick Ride works: search your GPRTU route, pick your seat, pay by Mobile Money, and get a QR e-ticket by SMS. Track your bus live across Ghana." path="/how-it-works" />
      <section className="hero-bg relative overflow-hidden">
        <div className="dot-grid absolute inset-0 opacity-50" />
        <Crest variant="navy" className="pointer-events-none absolute -right-24 -top-10 h-[420px] w-[420px] opacity-[0.04]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[1.1fr_1fr] lg:py-16">
          <div className="rise">
            <OfficialBadge />
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">
              Book a bus in <span className="text-[#0052FF]">under a minute</span>.
            </h1>
            <p className="mt-4 max-w-lg text-base text-slate-600 md:text-lg">From searching a route to boarding with a QR code — here's exactly how travelling with the official GPRTU platform works.</p>
            <Button onClick={() => nav("/")} className="mt-6 rounded-full bg-[#0052FF] px-6 font-bold text-white hover:bg-[#0041CC] btn-press" data-testid="how-cta-book">
              Book a trip <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="relative rise" style={{ animationDelay: ".1s" }}>
            <img src="/hero.jpg" alt="GPRTU intercity coach" loading="lazy" className="h-44 w-full rounded-[28px] object-cover shadow-[0_30px_60px_-30px_rgba(0,82,255,0.5)] sm:h-60 lg:h-72 lg:rounded-[32px]" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="space-y-3">
          {STEPS.map((s, idx) => <Step key={s.title} i={idx + 1} {...s} />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <h2 className="mb-5 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">Little things you'll love</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {EXTRAS.map((e) => (
            <div key={e.title} className="card-soft p-5" data-testid={`how-extra-${e.title}`}>
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-amber-50 text-amber-600"><e.icon className="h-5 w-5" /></span>
              <div className="mt-3 font-display font-bold text-slate-900">{e.title}</div>
              <p className="mt-1 text-sm text-slate-500">{e.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-col items-center gap-4 rounded-3xl bg-[#182678] p-8 text-center text-white">
          <FlagStrip className="h-1 w-24 overflow-hidden rounded-full" />
          <h3 className="font-display text-2xl font-extrabold">Ready to travel?</h3>
          <p className="max-w-md text-sm text-blue-100">Find your route, pick your seat and pay with Mobile Money right now.</p>
          <Button onClick={() => nav("/")} className="rounded-full bg-white px-6 font-bold text-[#0052FF] hover:bg-blue-50 btn-press" data-testid="how-cta-book-bottom">Search departures</Button>
        </div>
      </section>
    </div>
  );
}
